import { useEffect, useRef } from 'react';

// Re-runs `onRefresh` when the user returns to the tab/window (focus or the
// document becoming visible again). This catches the common "someone else
// edited the data while I was looking elsewhere, and my screen was stale until
// I manually refreshed" case — without the constant load of timed polling or
// the infrastructure of real-time push.
//
// It only ever calls the page's own existing loader, so it refreshes server
// data in place and does not remount the page or clear open modals/forms.
// Calls are throttled so a quick alt-tab in and out won't spam the API.
export default function useRefreshOnFocus(onRefresh, { throttleMs = 2000 } = {}) {
  const cbRef = useRef(onRefresh);
  cbRef.current = onRefresh;
  const lastRef = useRef(0);

  useEffect(() => {
    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRef.current < throttleMs) return;
      lastRef.current = now;
      try { cbRef.current?.(); } catch { /* page loader threw — ignore */ }
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') maybeRefresh(); };
    window.addEventListener('focus', maybeRefresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [throttleMs]);
}
